import { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { matchesSearch } from '@/utils/normalize';
import type { PosWarehouseStock } from '../types';

/** TCR-2575 — khớp theo tên HOẶC mã kho, bỏ dấu, không phân biệt hoa thường. */
export function filterWarehouses<T extends Pick<PosWarehouseStock, 'name' | 'code'>>(
  warehouses: T[],
  keyword: string,
): T[] {
  const trimmed = keyword.trim();
  if (!trimmed) return warehouses;
  return warehouses.filter(
    (w) => matchesSearch(trimmed, w.name ?? '') || matchesSearch(trimmed, w.code ?? ''),
  );
}

/** Từ khoá tìm kho — tự xoá khi dialog đóng để lần mở sau thấy lại đủ danh sách. */
export function useWarehouseKeyword(open: boolean) {
  const [keyword, setKeyword] = useState('');
  useEffect(() => {
    if (!open) setKeyword('');
  }, [open]);
  return [keyword, setKeyword] as const;
}

interface Props {
  open: boolean;
  value: string;
  onChange: (value: string) => void;
}

/** Modal antd tự đặt focus vào khung dialog khi mở xong — đợi qua mốc đó rồi mới focus ô tìm. */
const FOCUS_DELAY_MS = 300;

/**
 * TCR-2575 — ô tìm kho trên dialog "Chọn kho xuất hàng": chi nhánh có nhiều kho, trước
 * đây thu ngân phải cuộn danh sách mới thấy kho cần chọn.
 */
export function WarehouseSearchInput({ open, value, onChange }: Props) {
  const inputRef = useRef<InputRef>(null);
  // `autoFocus` không đủ: Modal giành lại focus sau khi mở, thu ngân vẫn phải bấm vào ô.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <Input
      ref={inputRef}
      allowClear
      placeholder="Tìm kho theo tên hoặc mã"
      prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ marginBottom: 8 }}
      data-testid="warehouse-picker-search"
    />
  );
}
